// Legal pages — Terms, Privacy, DPA.
// Plain English drafts. Counsel review required before public launch.

import Seo from '../components/Seo';

interface Props {
  page: 'terms' | 'privacy' | 'dpa';
}

const TITLES: Record<Props['page'], string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  dpa: 'Data Processing Addendum',
};

const SLUGS: Record<Props['page'], string> = {
  terms: '// legal · terms',
  privacy: '// legal · privacy',
  dpa: '// legal · dpa',
};

const LAST_UPDATED = '2026-07-11';

type Section = { h: string; body: string[] };

const TERMS: Section[] = [
  {
    h: '1. Who we are',
    body: [
      'WFS Chatbot is a customer messaging platform operated by Wavefront Studio LLC, the team behind resinrock.com. When these terms say "we", "us" or "Wavefront Studio", that is who we mean. When we say "you" or "Customer", we mean the business that signed up for an account.',
      'You can reach us at info@wavefrontstudiollc.com.',
    ],
  },
  {
    h: '2. The service',
    body: [
      'We provide a hosted platform that helps you receive, triage and reply to customer messages across channels like Instagram, Facebook, WhatsApp, TikTok, email, SMS and your own website. The platform can use AI to draft replies, classify messages, search your knowledge base and call tools you have connected (for example product lookup or order status).',
      'Access is gated. We review every request manually and reserve the right to approve, decline or pause an account at any time.',
    ],
  },
  {
    h: '3. Your account',
    body: [
      'You are responsible for keeping your login credentials secure, for everything done under your account, and for making sure the people you invite to your workspace are authorised to act on behalf of your business.',
      'If you suspect unauthorised access, tell us right away at info@wavefrontstudiollc.com.',
    ],
  },
  {
    h: '4. Acceptable use',
    body: [
      'You will not use the platform to send spam, harass anyone, infringe intellectual property, distribute malware, attempt to break our security, or do anything that violates the laws or platform rules of the channels you connect (for example Meta, TikTok or your SMS carrier).',
      'You will not try to extract our model prompts, reverse engineer the service, or use it to build a competing product.',
    ],
  },
  {
    h: '5. Plans, limits and billing',
    body: [
      'During the early access period there is no public price list. Plans are assigned manually when your account is approved and may include caps on businesses, channels, conversations, AI messages, team members and other resources.',
      'If you exceed your plan limits we may throttle, queue or pause AI replies until usage drops, the next billing period starts, or you upgrade. We will tell you before doing this whenever practical.',
      'When paid plans launch, the price, billing cycle and payment terms will be shown to you in writing before you are charged. You may cancel at any time. Cancellation stops future charges but does not refund the current period unless we agree otherwise.',
    ],
  },
  {
    h: '6. Your content',
    body: [
      'You own your content. That includes the messages your customers send you, your knowledge base, your brand voice settings, your team accounts and anything you upload.',
      'You grant us a limited licence to host, process, transmit and display that content strictly to operate the service for you. We do not sell your content. We do not use your content to train third-party foundation models.',
    ],
  },
  {
    h: '7. AI output',
    body: [
      'The platform uses AI models to generate suggested replies, summaries and classifications. AI output can be wrong. You are responsible for reviewing AI replies before they reach a customer when you operate in human or hybrid mode, and for setting guardrails appropriate to your business when you operate in auto mode.',
      'We are not liable for the content of AI replies you choose to send.',
    ],
  },
  {
    h: '8. Service availability',
    body: [
      'We work hard to keep the service available, but we do not guarantee uptime during early access. The service is provided "as is" without warranties of any kind. We may add, change or remove features.',
    ],
  },
  {
    h: '9. Suspension and termination',
    body: [
      'We may suspend or terminate your account if you violate these terms, if your account creates legal risk for us, or if you go more than 60 days without responding to a billing or compliance request. You may close your account at any time by emailing info@wavefrontstudiollc.com.',
      'On termination we will give you a reasonable window to export your data before deletion. See section 11.',
    ],
  },
  {
    h: '10. Liability',
    body: [
      'To the maximum extent permitted by law, our total liability to you for any claim arising out of or relating to the service is limited to the amounts you paid us in the twelve months preceding the claim, or one hundred US dollars, whichever is greater. We are not liable for indirect, incidental, consequential, special or punitive damages, or for lost profits, lost revenue or lost data.',
    ],
  },
  {
    h: '11. Data export and deletion',
    body: [
      'You can request an export of your conversation history, contacts and knowledge base at any time from inside the dashboard or by emailing us. After your account is closed we keep your data for 30 days so you can recover it, and then delete it from production systems. Backups are purged on the regular backup rotation, normally within 90 days.',
      'If you are an end customer who messaged a business that uses WFS Chatbot on Facebook, Instagram or WhatsApp, you can request deletion of your contact records and message history at https://ai.wavefrontstudiollc.com/data-deletion. The same page describes how to remove the app from your Meta account, which automatically invalidates the channel\'s access tokens on our side.',
    ],
  },
  {
    h: '12. Changes to these terms',
    body: [
      'We may update these terms. If a change is material we will email account owners at least 14 days before it takes effect. Continued use after the effective date means you accept the change.',
    ],
  },
  {
    h: '13. Governing law',
    body: [
      'These terms are governed by the laws of the jurisdiction where Wavefront Studio LLC is incorporated. Any dispute will be resolved in the courts of that jurisdiction. The specific jurisdiction will be added before public launch and counsel review.',
    ],
  },
];

const PRIVACY: Section[] = [
  {
    h: '1. Scope',
    body: [
      'This policy explains what personal information WFS Chatbot collects, how we use it, who we share it with, where it lives, how long we keep it, and what rights you have. It applies to this website, the dashboard, the WFS Chatbot mobile app for iOS and Android, the embeddable chat widget and the API.',
      'If you are a customer of one of our customers (for example you message a shop that uses WFS Chatbot), the shop is the data controller for your messages. We process them on their behalf as described in our Data Processing Addendum.',
    ],
  },
  {
    h: '2. What we collect',
    body: [
      'Account data: name, work email, business name, country, password hash, role, and answers you give on the request access form.',
      'Usage data: which pages you visit in the dashboard, when you log in, and aggregate counts of conversations and AI messages so we can enforce plan limits.',
      'Customer messages: the content of messages your customers send you, normalised across channels, plus contact identifiers like channel-specific user IDs, names if available, and tags you apply.',
      'Knowledge base: anything you choose to upload or have us crawl from your public website, used to ground AI replies.',
      'Technical data: IP address, user agent, request IDs and error logs, kept for security and debugging.',
      'Mobile device data: if you use the WFS Chatbot mobile app and turn on notifications, a push notification token and your device platform (iOS or Android). See the mobile app section below.',
    ],
  },
  {
    h: '3. Why we use it',
    body: [
      'To run the service you signed up for. To authenticate you. To enforce plan limits and prevent abuse. To send transactional emails (request received, approved, password reset, magic links, billing). To improve the product based on aggregate usage. To respond to you when you contact support. To meet legal obligations.',
      'We do not sell your personal information. We do not use customer message content to train third-party foundation models. We do not share your data with advertisers.',
    ],
  },
  {
    h: '4. AI model providers',
    body: [
      'AI replies are generated by models in the Anthropic Claude family. When the platform calls a model, the request includes the specific message, the relevant knowledge base context, your brand voice settings and conversation history needed to produce a reply. Anthropic processes that request as our subprocessor under their commercial terms, which prohibit using customer data to train their models.',
      'We may add or change model providers in the future. The current list is kept up to date in section 9.',
    ],
  },
  {
    h: '5. Meta Platforms (Facebook, Instagram, WhatsApp)',
    body: [
      'When you connect a Facebook Page, an Instagram Business account or a WhatsApp Business number to WFS Chatbot, we ask Meta for the specific permissions needed to send and receive messages on your behalf. We request only what is necessary for the features you turn on.',
      'Permissions we request and what each one is used for:',
      '• pages_messaging — send and receive Facebook Page direct messages.',
      '• pages_show_list — list the Pages you manage so you can pick which one to connect.',
      '• pages_manage_metadata — register and manage the webhook subscription that delivers your Page\'s messages to us.',
      '• pages_read_engagement — read comments on your Page posts so the comment-to-DM automation can react to them.',
      '• pages_manage_engagement — reply to comments on your Page posts when an automation is configured to do so.',
      '• instagram_basic — read the connected Instagram Business profile so the dashboard shows the right account.',
      '• instagram_manage_messages — send and receive Instagram direct messages.',
      '• instagram_manage_comments — read and reply to comments on your Instagram posts when a comment-to-DM automation is configured.',
      '• whatsapp_business_management — list and manage the WhatsApp Business Accounts and templates connected to your business.',
      '• whatsapp_business_messaging — send and receive WhatsApp messages on the connected number.',
      '• business_management — discover the Business Assets (Pages, IG accounts, WABAs) you can connect during the OAuth flow.',
      '• human_agent — when a human agent on your team replies outside the standard 24-hour window, this permission lets us tell Meta the message is from a human so it is allowed under the 7-day human-agent window.',
      'What we receive from Meta when you connect: the access token for the selected Page or WABA, the Page or account ID, your display name, and the Meta user_id of the person who completed the connect (used so that if that person later removes our app, we can find and disable the right channels).',
      'What we send to Meta on outbound messages: the recipient\'s Meta user_id (or phone number for WhatsApp), the message text or selected template, and standard delivery metadata such as messaging_type and message tags.',
      'Meta Platforms, Inc. is one of our subprocessors when you use these channels (see section 9). We do not use Meta connections for anything beyond what is described above. We do not advertise. We do not enrich profiles. We do not retain message data longer than the retention windows in section 8.',
      'Disconnecting and deletion: to disconnect, go to Facebook Settings → Business Integrations (or the equivalent panel on Instagram or in WhatsApp Manager) and remove WFS Chatbot. Meta will notify us automatically and we will invalidate the access tokens immediately. To also delete the contact records and message history we hold for you, visit https://ai.wavefrontstudiollc.com/data-deletion for the three available paths, or submit a Data Deletion Request through Meta\'s app removal panel — Meta forwards it to us and we process it automatically.',
    ],
  },
  {
    h: '6. WFS Chatbot mobile app',
    body: [
      'WFS Chatbot is our companion app for iOS and Android, used by team members of businesses on the platform to read and answer customer conversations. You sign in with the same account you use on the dashboard. The app has no self-serve sign-up: accounts are created and removed by your workspace owner, and an owner can delete a team member account at any time from the dashboard (Team settings) or by emailing info@wavefrontstudiollc.com.',
      'Push notifications: if you grant notification permission, the app registers a device push token with your account so we can alert you about new messages and escalations. The token is linked to your team account, used only to deliver these notifications, and removed when you sign out or disable notifications. The app is fully functional if you decline notification permission.',
      'Biometric app lock: you can optionally lock the app behind Face ID, Touch ID or your device biometrics. The biometric check is performed entirely by your device\'s operating system. Biometric data never leaves your device and is never sent to or stored by us; we only store your on/off preference locally on the device.',
      'Attachments: files you attach to a conversation from the app are uploaded to our file storage and become part of that conversation, with the same retention as customer messages.',
      'Credentials: your session tokens are stored in the device\'s secure keychain (iOS) or keystore (Android).',
      'The mobile app contains no advertising, no third-party analytics or tracking SDKs, and does not access your advertising identifier.',
    ],
  },
  {
    h: '7. Where data is stored',
    body: [
      'The platform runs on Cloudflare infrastructure. Data is stored in Cloudflare D1 (relational database), Cloudflare KV (cache), Cloudflare R2 (file storage) and Cloudflare Vectorize (knowledge base embeddings). Cloudflare operates global infrastructure and may process data in regions outside your country. If you have specific data residency requirements, contact us before approval.',
    ],
  },
  {
    h: '8. How long we keep it',
    body: [
      'Account data: for the life of the account, plus 30 days after closure for recovery, then deleted.',
      'Customer messages: for the life of the account or until you delete them. After account closure, deleted on the same 30 day cycle. Closed conversations may be archived to cold storage after 90 days of inactivity.',
      'Logs and technical data: 30 days for application logs, 90 days for security and audit logs.',
      'Backups: rotated regularly, fully purged within 90 days of deletion from production.',
      'Meta data deletion and deauthorization callbacks are processed automatically: contact records tied to the requesting Meta user_id are anonymized within minutes, and channel access tokens are invalidated immediately.',
    ],
  },
  {
    h: '9. Subprocessors',
    body: [
      'We use a small number of trusted vendors to run the service. The current list is:',
      '• Cloudflare (infrastructure: compute, storage, DNS, edge caching)',
      '• Anthropic (AI model provider for the Claude family)',
      '• Meta Platforms, Inc. (only when you connect Facebook, Instagram or WhatsApp — for messaging delivery, account discovery, webhook subscription, and template management)',
      '• An email delivery provider for transactional email (current provider listed in the dashboard under Settings → About)',
      'We will give 30 days notice before adding a new subprocessor that processes customer message content.',
    ],
  },
  {
    h: '10. Your rights',
    body: [
      'Depending on where you live, you may have the right to access, correct, export or delete the personal information we hold about you, to object to or restrict certain processing, and to lodge a complaint with a data protection authority. To exercise any of these rights, email info@wavefrontstudiollc.com from the address tied to your account. We respond within 30 days.',
      'If you are a customer of one of our customers (for example, you messaged a shop on Facebook, Instagram or WhatsApp that uses WFS Chatbot) and you want your data deleted, see https://ai.wavefrontstudiollc.com/data-deletion for three direct paths: removing the app from your Meta account, submitting a Data Deletion Request through Meta, or emailing info@wavefrontstudiollc.com.',
    ],
  },
  {
    h: '11. Cookies',
    body: [
      'The dashboard uses a small number of strictly necessary cookies to keep you logged in and remember your workspace. The marketing site uses Cloudflare Web Analytics, which is cookieless. We do not use third-party advertising cookies.',
    ],
  },
  {
    h: '12. Children',
    body: [
      'The service is not intended for anyone under 16. We do not knowingly collect personal information from children.',
    ],
  },
  {
    h: '13. Contact',
    body: [
      'For privacy questions or to exercise your rights, email info@wavefrontstudiollc.com.',
      'For data deletion requests, see https://ai.wavefrontstudiollc.com/data-deletion.',
    ],
  },
];

const DPA: Section[] = [
  {
    h: '1. Purpose',
    body: [
      'This Data Processing Addendum forms part of the Terms of Service between WFS Chatbot ("Processor") and the Customer ("Controller") and applies whenever WFS Chatbot processes personal data on the Controller\'s behalf in the course of providing the service.',
    ],
  },
  {
    h: '2. Roles',
    body: [
      'For customer messages, knowledge base content and contact records that the Controller brings into the platform, the Controller is the data controller and WFS Chatbot is the data processor. For account data about the Controller\'s own users (their team members), WFS Chatbot is also a controller for the limited purposes of authentication, billing and support.',
    ],
  },
  {
    h: '3. Scope of processing',
    body: [
      'Subject matter: provision of the customer messaging platform. Duration: for the term of the agreement and any post-termination retention period. Nature: hosting, transmission, classification, semantic search, AI-assisted drafting and analytics. Categories of data subjects: the Controller\'s end customers, employees and authorised contacts. Categories of personal data: identifiers (name, email, channel ID, phone where provided), message content, metadata, and any data the Controller chooses to upload to the knowledge base.',
    ],
  },
  {
    h: '4. Processor obligations',
    body: [
      'WFS Chatbot will: process personal data only on the Controller\'s documented instructions, including the configuration choices made in the dashboard; ensure that personnel with access are bound by confidentiality; implement appropriate technical and organisational security measures (encryption in transit, encryption of credentials at rest, scoped access, audit logging, principle of least privilege); assist the Controller with data subject requests; notify the Controller without undue delay of any personal data breach; and delete or return personal data at the end of the agreement subject to the retention windows in the Privacy Policy.',
    ],
  },
  {
    h: '5. Subprocessors',
    body: [
      'The Controller authorises WFS Chatbot to engage subprocessors. The current list is published in the Privacy Policy section 9. WFS Chatbot will give the Controller at least 30 days notice before adding a new subprocessor that processes customer message content. The Controller may object on reasonable grounds; if the parties cannot agree, the Controller may terminate the affected portion of the service.',
    ],
  },
  {
    h: '6. International transfers',
    body: [
      'Where personal data is transferred outside the Controller\'s region, the parties rely on the appropriate transfer mechanism (for example Standard Contractual Clauses) and on the safeguards already in place between WFS Chatbot and its infrastructure providers.',
    ],
  },
  {
    h: '7. Audits',
    body: [
      'WFS Chatbot will make available, on reasonable request and no more than once per year, sufficient information to demonstrate compliance with this Addendum. On-site audits are available where required by law and at the Controller\'s expense.',
    ],
  },
  {
    h: '8. Liability',
    body: [
      'Each party\'s liability under this Addendum is subject to the limitations of liability in the Terms of Service.',
    ],
  },
  {
    h: '9. Order of precedence',
    body: [
      'In the event of a conflict between this Addendum and the Terms of Service, this Addendum prevails for matters relating to the processing of personal data.',
    ],
  },
];

const CONTENT: Record<Props['page'], Section[]> = {
  terms: TERMS,
  privacy: PRIVACY,
  dpa: DPA,
};

const INTROS: Record<Props['page'], string> = {
  terms:
    'Plain language. These terms govern your use of WFS Chatbot. By creating an account or using the service you agree to them.',
  privacy:
    'Plain language. This policy explains what personal information we collect, how we use it, and the rights you have over it.',
  dpa:
    'Plain language. This addendum sets out how we process personal data on your behalf when you use WFS Chatbot.',
};

export default function Legal({ page }: Props) {
  const sections = CONTENT[page];

  return (
    <section className="bg-ink text-white">
      <Seo
        title={`${TITLES[page]} · WFS Chatbot`}
        description={INTROS[page]}
        path={`/legal/${page}`}
      />
      <div className="mx-auto max-w-prose px-6 py-20 md:py-28">
        <span className="rr-tag">{SLUGS[page]}</span>
        <h1 className="mt-4 text-display-md font-bold tracking-headline text-white">
          {TITLES[page]}
        </h1>
        <p className="mt-2 font-mono text-mono-xs uppercase tracking-mono text-graphite">
          last updated: {LAST_UPDATED}
        </p>
        <p className="mt-6 text-base text-ash">{INTROS[page]}</p>

        <div className="mt-12 space-y-10">
          {sections.map((s) => (
            <div key={s.h}>
              <h2 className="text-lg font-semibold tracking-headline text-white">{s.h}</h2>
              <div className="mt-3 space-y-3">
                {s.body.map((p, i) => (
                  <p key={i} className="text-sm leading-relaxed text-ash">
                    {p}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 h-px bg-ink-3" aria-hidden />
        <p className="mt-6 text-sm text-ash">
          Questions?{' '}
          <a className="text-signal underline" href="mailto:info@wavefrontstudiollc.com">
            info@wavefrontstudiollc.com
          </a>
        </p>
      </div>
    </section>
  );
}
